import type { Camera } from '../../services/camerasApi';
import type { OverviewControlTopPattern } from './overviewTypes';

export type PatternReviewLinkParams = {
  cameraId: string;
  deviceId: string;
  incidentType: string;
};

/** Parse camera/device hints from pattern review deep links. */
export function parsePatternReviewLinkParams(link: string | null | undefined): PatternReviewLinkParams | null {
  const raw = String(link || '').trim();
  if (!raw) return null;
  try {
    const url = raw.startsWith('/') ? new URL(raw, 'https://overview.local') : new URL(raw);
    const cameraId = (url.searchParams.get('camera_id') || '').trim();
    const deviceId = (url.searchParams.get('device_id') || '').trim();
    const incidentType = (url.searchParams.get('incident_type') || '').trim();
    if (!cameraId && !deviceId) return null;
    return { cameraId, deviceId, incidentType };
  } catch {
    return null;
  }
}

/** Count top critical-risk patterns linked to each camera (when review links include camera_id). */
export function countLinkedCriticalPatternsByCamera(
  topPatterns: OverviewControlTopPattern[] | null | undefined,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const pattern of topPatterns || []) {
    const params = parsePatternReviewLinkParams(pattern.linkToPatternReview);
    const cameraId = params?.cameraId;
    if (!cameraId) continue;
    counts[cameraId] = (counts[cameraId] || 0) + 1;
  }
  return counts;
}

export function isCameraEnabledForEvidence(camera: Camera): boolean {
  if (camera.enabled === false) return false;
  const status = String(camera.status || '').toLowerCase();
  return status !== 'disabled';
}

/** Recent API-reported frame or freshly fetched thumbnail (15m). */
export const RECENT_FRAME_MAX_AGE_MS = 15 * 60 * 1000;

export function isCameraOnlineForEvidence(camera: Camera): boolean {
  return cameraEvidenceStatusMeta(camera).label === 'Online';
}

export function isCameraOfflineForDisplay(camera: Camera): boolean {
  return cameraEvidenceStatusMeta(camera).label === 'Offline';
}

export function isCameraReadyForLiveViewStream(camera: Camera): boolean {
  const status = String(camera.status || '').trim().toLowerCase();
  const runtimeStatus = String(camera.health?.status || '').trim().toLowerCase();
  const healthState = String(camera.health_state?.state || '').trim().toUpperCase();
  return (
    status === 'running'
    || status === 'ready'
    || runtimeStatus === 'running'
    || runtimeStatus === 'ready'
    || healthState === 'READY'
  );
}

/**
 * Whether Spatial Operations / Live View may attempt raw MJPEG.
 * false = explicit disabled or authoritative offline (do not start a stream).
 * true = Live View already treats the camera as stream-ready.
 * null = unknown/stale/noncanonical registry status; MJPEG may still prove live.
 */
export function cameraOnlineForLiveStreamAttempt(camera: Camera | null | undefined): boolean | null {
  if (!camera) return null;
  if (!isCameraEnabledForEvidence(camera)) return false;
  if (isCameraReadyForLiveViewStream(camera)) return true;
  const status = String(camera.status || '').trim().toLowerCase();
  const healthState = String(camera.health_state?.state || '').trim().toUpperCase();
  if (status === 'offline' || healthState === 'OFFLINE') return false;
  return null;
}

export type LiveViewUrlPin = {
  cameraId?: string | null;
  deviceId?: string | null;
};

export function cameraMatchesLiveViewUrlPin(camera: Camera, pin?: LiveViewUrlPin): boolean {
  const cameraId = String(pin?.cameraId || '').trim();
  const deviceId = String(pin?.deviceId || '').trim();
  if (!cameraId && !deviceId) return false;
  if (cameraId && camera.camera_id !== cameraId) return false;
  if (deviceId && camera.edge_device_id !== deviceId) return false;
  return true;
}

export type LiveViewCameraDisplayContext = {
  showOffline: boolean;
  pinToUrl?: LiveViewUrlPin;
  hasPoster: boolean;
  hasStreamUrl: boolean;
};

/** Live View tile visibility (offline hidden by default; Overview-aligned). */
export function shouldDisplayCameraInLiveView(
  camera: Camera,
  ctx: LiveViewCameraDisplayContext,
): boolean {
  const pinned = cameraMatchesLiveViewUrlPin(camera, ctx.pinToUrl);
  const offline = isCameraOfflineForDisplay(camera);

  if (!ctx.showOffline && offline && !pinned) {
    return false;
  }
  if (ctx.showOffline && offline) {
    return true;
  }

  return isCameraReadyForLiveViewStream(camera) || ctx.hasPoster || ctx.hasStreamUrl;
}

export function hasRecentFrameTimestamp(camera: Camera, nowMs: number = Date.now()): boolean {
  const ts = cameraLastFrameTimestamp(camera);
  if (!ts) return false;
  const parsed = Date.parse(ts);
  if (Number.isNaN(parsed)) return false;
  return nowMs - parsed <= RECENT_FRAME_MAX_AGE_MS;
}

export function hasCameraThumbnail(
  thumbnails: Record<string, string>,
  cameraId: string,
): boolean {
  return Boolean(thumbnails[cameraId]);
}

/** Camera API data URLs and Investigate evidence blob URLs. Reject JSON/error bodies. */
export function isUsableThumbnailSrc(value: string | null | undefined): boolean {
  const raw = String(value || '').trim();
  return /^data:image\//i.test(raw) || /^blob:/i.test(raw);
}

/**
 * Default visible: online, or a fetched snapshot exists.
 * Offline cameras with only a stale registry last_frame_at stay hidden.
 */
export function isUsableCameraEvidence(
  camera: Camera,
  thumbnails: Record<string, string>,
): boolean {
  if (hasCameraThumbnail(thumbnails, camera.camera_id)) return true;
  return isCameraOnlineForEvidence(camera);
}

export function isHiddenOfflineNoFrameCamera(
  camera: Camera,
  thumbnails: Record<string, string>,
): boolean {
  return !isUsableCameraEvidence(camera, thumbnails);
}

/** Summary/count: real snapshot, or online with a recent API frame timestamp. */
export function cameraHasRecentEvidence(
  camera: Camera,
  thumbnails: Record<string, string>,
): boolean {
  if (hasCameraThumbnail(thumbnails, camera.camera_id)) return true;
  return isCameraOnlineForEvidence(camera) && hasRecentFrameTimestamp(camera);
}

export type CameraEvidencePartition = {
  usable: Camera[];
  hidden: Camera[];
  /** Usable cameras that already have a thumbnail or recent frame timestamp. */
  withRecentEvidenceCount: number;
};

export function compareUsableCamerasForEvidence(
  a: Camera,
  b: Camera,
  linkedByCamera: Record<string, number>,
  thumbnails: Record<string, string>,
): number {
  const linkedDelta = (linkedByCamera[b.camera_id] || 0) - (linkedByCamera[a.camera_id] || 0);
  if (linkedDelta !== 0) return linkedDelta;

  const evidenceDelta =
    Number(cameraHasRecentEvidence(b, thumbnails)) - Number(cameraHasRecentEvidence(a, thumbnails));
  if (evidenceDelta !== 0) return evidenceDelta;

  const onlineDelta = Number(isCameraOnlineForEvidence(b)) - Number(isCameraOnlineForEvidence(a));
  if (onlineDelta !== 0) return onlineDelta;

  return compareCamerasForEvidenceDisplay(a, b);
}

export function partitionCameraEvidenceSources(
  cameras: Camera[],
  thumbnails: Record<string, string>,
  linkedByCamera: Record<string, number>,
): CameraEvidencePartition {
  const enabled = cameras.filter(isCameraEnabledForEvidence);
  const usable: Camera[] = [];
  const hidden: Camera[] = [];

  for (const camera of enabled) {
    if (isUsableCameraEvidence(camera, thumbnails)) {
      usable.push(camera);
    } else {
      hidden.push(camera);
    }
  }

  usable.sort((a, b) => compareUsableCamerasForEvidence(a, b, linkedByCamera, thumbnails));
  hidden.sort(compareCamerasForEvidenceDisplay);

  return {
    usable,
    hidden,
    withRecentEvidenceCount: usable.filter((camera) => cameraHasRecentEvidence(camera, thumbnails)).length,
  };
}

export function buildCameraEvidenceSummaryLine(usableCount: number, hiddenCount: number): string {
  const lead = `${usableCount.toLocaleString()} camera${usableCount === 1 ? '' : 's'} with recent evidence`;
  if (hiddenCount <= 0) return lead;
  return `${lead} · ${hiddenCount.toLocaleString()} offline/no-frame camera${
    hiddenCount === 1 ? '' : 's'
  } tracked in System Health`;
}

export function sortCamerasForThumbnailFetch(
  cameras: Camera[],
  linkedByCamera: Record<string, number>,
): Camera[] {
  return [...cameras]
    .filter(isCameraEnabledForEvidence)
    .sort((a, b) => {
      const linkedDelta = (linkedByCamera[b.camera_id] || 0) - (linkedByCamera[a.camera_id] || 0);
      if (linkedDelta !== 0) return linkedDelta;
      return compareCamerasForEvidenceDisplay(a, b);
    });
}

export function compareCamerasForEvidenceDisplay(a: Camera, b: Camera): number {
  const rank = (camera: Camera) => {
    const status = String(camera.status || 'unknown').toLowerCase();
    if (status === 'running') return 0;
    if (status === 'ready') return 1;
    if (status === 'deploying') return 2;
    if (status === 'unknown') return 3;
    if (status === 'offline') return 4;
    if (status === 'failed') return 5;
    return 6;
  };
  const delta = rank(a) - rank(b);
  if (delta !== 0) return delta;
  return String(a.name || a.camera_id).localeCompare(String(b.name || b.camera_id));
}

/** Match cloud_api camera health classification (90s device heartbeat threshold). */
const DEVICE_HEARTBEAT_OFFLINE_THRESHOLD_S = 90;

export function cameraEvidenceStatusMeta(camera: Camera): {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default' | 'info';
} {
  const status = String(camera.status || 'unknown').toLowerCase();
  const health = String(camera.health_state?.state || '').toUpperCase();
  const deviceStatus = String(camera.health_state?.device_status || '').toLowerCase();
  const heartbeatAgeS = camera.health_state?.age_s;

  // Prefer health_state over stale registry/runtime status (bootstrap can leave status=running).
  if (health === 'OFFLINE') return { label: 'Offline', color: 'error' };
  if (health === 'AUTH_FAILED') return { label: 'Failed', color: 'error' };
  if (health === 'DEGRADED' || health === 'STREAM_ERROR') return { label: 'Degraded', color: 'warning' };
  if (health === 'READY') return { label: 'Online', color: 'success' };

  const deviceOffline =
    Boolean(deviceStatus) && !['online', 'running'].includes(deviceStatus);
  const heartbeatStale =
    typeof heartbeatAgeS === 'number' && heartbeatAgeS > DEVICE_HEARTBEAT_OFFLINE_THRESHOLD_S;

  if (deviceOffline || heartbeatStale) {
    return { label: 'Offline', color: 'error' };
  }

  if (status === 'offline' || status === 'failed') return { label: 'Offline', color: 'error' };
  if (status === 'deploying') return { label: 'Deploying', color: 'info' };
  if (status === 'disabled') return { label: 'Disabled', color: 'default' };
  if (status === 'running' || status === 'ready') return { label: 'Online', color: 'success' };
  return { label: 'Unknown', color: 'default' };
}

export function cameraLastFrameTimestamp(camera: Camera): string | null {
  const raw =
    camera.health_state?.last_frame_at ||
    camera.health?.last_frame ||
    null;
  const text = String(raw || '').trim();
  return text || null;
}

export function formatCameraLastFrameLabel(
  isoTimestamp: string | null,
  options?: { offline?: boolean },
): string {
  if (options?.offline) return 'Last frame: unavailable while offline';
  if (!isoTimestamp) return 'Last frame: unknown';
  const parsed = Date.parse(isoTimestamp);
  if (Number.isNaN(parsed)) return `Last frame: ${isoTimestamp}`;
  const ageMs = Date.now() - parsed;
  if (ageMs < 60_000) return 'Last frame: just now';
  if (ageMs < 3_600_000) {
    const mins = Math.max(1, Math.round(ageMs / 60_000));
    return `Last frame: ${mins}m ago`;
  }
  if (ageMs < 86_400_000) {
    const hours = Math.max(1, Math.round(ageMs / 3_600_000));
    return `Last frame: ${hours}h ago`;
  }
  try {
    return `Last frame: ${new Date(parsed).toLocaleString()}`;
  } catch {
    return `Last frame: ${isoTimestamp}`;
  }
}

export function buildCameraViewPath(camera: Camera): string {
  const params = new URLSearchParams();
  if (camera.camera_id) params.set('camera_id', camera.camera_id);
  if (camera.edge_device_id) params.set('device_id', camera.edge_device_id);
  const qs = params.toString();
  return qs ? `/cameras?${qs}` : '/cameras';
}

/** Overview should not hammer the snapshot API; fetch thumbnails for the first N active cameras. */
export const OVERVIEW_CAMERA_THUMBNAIL_LIMIT = 8;
